/**
 * Performance Monitoring Utility
 * Tracks key performance metrics for first-time users
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      timeToFirstPaint: null,
      timeToInteractive: null,
      firestoreInitTime: null,
      firstDataFetchTime: null,
      renderBlockingSources: [],
      apiFetchTimes: {}
    };
    this.observers = [];
    this.startTime = performance.now();
  }

  /**
   * Initialize performance monitoring
   */
  init() {
    // Measure First Paint
    if ('PerformanceObserver' in window) {
      try {
        const paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name === 'first-contentful-paint') {
              this.metrics.timeToFirstPaint = entry.startTime;
              this.logMetric('First Paint', entry.startTime);
            }
          }
        });
        paintObserver.observe({ entryTypes: ['paint'] });
        this.observers.push(paintObserver);
      } catch (e) {
        console.warn('PerformanceObserver not supported:', e);
      }

      // Measure Long Tasks (render blocking)
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              this.metrics.renderBlockingSources.push({
                name: entry.name || 'Unknown',
                duration: entry.duration,
                startTime: entry.startTime
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
        this.observers.push(longTaskObserver);
      } catch (e) {
        // Long task observer not supported in all browsers
      }
    }

    // Measure page load
    window.addEventListener('load', () => {
      const loadTime = performance.now() - this.startTime;
      this.metrics.timeToInteractive = loadTime;
      this.logMetric('Page Load', loadTime);
    });
  }

  /**
   * Mark Firestore initialization time
   */
  markFirestoreInit(duration) {
    this.metrics.firestoreInitTime = duration;
    this.logMetric('Firestore Init', duration);
  }

  /**
   * Mark first data fetch time
   */
  markFirstDataFetch(duration, source = 'unknown') {
    if (!this.metrics.firstDataFetchTime) {
      this.metrics.firstDataFetchTime = duration;
      this.logMetric('First Data Fetch', duration, source);
    }
  }

  /**
   * Track API fetch time
   */
  trackAPIFetch(endpoint, duration) {
    this.metrics.apiFetchTimes[endpoint] = duration;
  }

  /**
   * Log metric to console (development only)
   */
  logMetric(name, value, source = '') {
    if (process.env.NODE_ENV === 'development') {
      const sourceText = source ? ` (${source})` : '';
      console.log(`⚡ Performance: ${name}${sourceText} - ${value.toFixed(2)}ms`);
    }
  }

  /**
   * Get all metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      totalTime: performance.now() - this.startTime
    };
  }

  /**
   * Report metrics (can be sent to analytics)
   */
  reportMetrics() {
    const metrics = this.getMetrics();
    
    // Log summary
    console.group('📊 Performance Metrics');
    console.table({
      'First Paint': `${metrics.timeToFirstPaint?.toFixed(2) || 'N/A'}ms`,
      'Page Load': `${metrics.timeToInteractive?.toFixed(2) || 'N/A'}ms`,
      'Firestore Init': `${metrics.firestoreInitTime?.toFixed(2) || 'N/A'}ms`,
      'First Data Fetch': `${metrics.firstDataFetchTime?.toFixed(2) || 'N/A'}ms`,
      'Total Time': `${metrics.totalTime.toFixed(2)}ms`
    });

    if (metrics.renderBlockingSources.length > 0) {
      console.warn('⚠️ Render Blocking Sources:', metrics.renderBlockingSources);
    }

    if (Object.keys(metrics.apiFetchTimes).length > 0) {
      console.log('📡 API Fetch Times:', metrics.apiFetchTimes);
    }
    console.groupEnd();

    // Send to analytics if needed
    if (window.gtag) {
      window.gtag('event', 'performance_metrics', {
        time_to_first_paint: metrics.timeToFirstPaint,
        time_to_interactive: metrics.timeToInteractive,
        firestore_init_time: metrics.firestoreInitTime,
        first_data_fetch_time: metrics.firstDataFetchTime
      });
    }
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (e) {
        // Ignore cleanup errors
      }
    });
    this.observers = [];
  }
}

// Singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;

